import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { MeResponse, User } from '@tasks/contracts';

import { logout as logoutRequest } from '../api/auth';
import { queryKeys } from '../api/queryKeys';
import { fetchMe } from '../api/auth';
import { ApiError } from '../lib/errors';
import { useSessionStore } from '../stores/sessionStore';

export type AuthState = {
  readonly user: User | null;
  readonly projects: MeResponse['projects'];
  readonly isLoading: boolean;
  /** Сессия не подтверждена (нет cookie) — это не ошибка приложения. */
  readonly isAuthenticated: boolean;
  readonly mustChangePassword: boolean;
  refetch: () => void;
  signOut: () => Promise<void>;
  setUser: (user: User) => void;
};

const AuthContext = createContext<AuthState | null>(null);

/**
 * Контекст сессии (UC-AUTH-03): `/api/me` запрашивается один раз при старте,
 * защищённые роуты не рендерятся, пока ответ не получен, — без мигания логина.
 */
export function AuthProvider({ children }: { readonly children: ReactNode }) {
  const queryClient = useQueryClient();
  const setSessionUser = useSessionStore((state) => state.setUser);

  const query = useQuery<MeResponse, Error>({
    queryKey: queryKeys.me,
    queryFn: ({ signal }) => fetchMe(signal),
    retry: false,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const user = query.data?.user ?? null;

  // Держим zustand-стор в синхроне: realtime-слой читает его императивно.
  if (user && useSessionStore.getState().user?.id !== user.id) {
    setSessionUser(user);
  }

  const signOut = useCallback(async () => {
    try {
      await logoutRequest();
    } finally {
      setSessionUser(null);
      queryClient.clear();
      window.location.assign('/login');
    }
  }, [queryClient, setSessionUser]);

  const setUser = useCallback(
    (next: User) => {
      setSessionUser(next);
      queryClient.setQueryData<MeResponse>(queryKeys.me, (previous) =>
        previous ? { ...previous, user: next } : { user: next, projects: [] },
      );
    },
    [queryClient, setSessionUser],
  );

  const value = useMemo<AuthState>(() => {
    const isUnauthenticated = query.error instanceof ApiError && query.error.isUnauthenticated();
    return {
      user,
      projects: query.data?.projects ?? [],
      isLoading: query.isPending,
      isAuthenticated: user !== null && !isUnauthenticated,
      mustChangePassword: user?.mustChangePassword ?? false,
      refetch: () => {
        void query.refetch();
      },
      signOut,
      setUser,
    };
  }, [user, query, signOut, setUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth нужно вызывать внутри AuthProvider');
  }
  return context;
}
